/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {
  AugmentedBrakeData,
  AugmentedSmarterTrailerSensorData,
  AugmentedSmarterTrailerSensorProperty,
  BaseAsset,
  BrakeChamberStatusType,
  CircuitStatus,
  CircuitSummary,
  DoorHistoryItem,
  GenericObject,
  SensorTypes,
  SensorUnits,
  SmarterTrailerIssueTypes,
  TrailerDoorStatus
} from '@ion/core/src/types/entities';
import {kgToLbs, lbsToKg} from '@ion/core/src/units/conversions';
import {Unit} from '@ion/core/src/units/units';
import {
  getMaximumBy,
  getMinimumBy
} from '@ion/core/src/utils/MinMaxUtils';
import {SmarterTrailerWidgetInfo} from '../../types/types';

export interface PropertyType {
  item: AugmentedSmarterTrailerSensorData | AugmentedSmarterTrailerSensorProperty;
  property: string;
  issues: SmarterTrailerIssueTypes[];
  includeMin?: boolean;
}

export enum WeightUnit {
  kg = 'kg',
  lbs = 'lbs'
};

/**
 * Defines a utility to return Door status value
 *
 * ```typescript
 * import {getDoorStatusValue} from '@ion/dls/src/components/smarterTrailer/helper';
 *
 * const value = getDoorStatusValue(status);
 *
 * ```
 */
export const getDoorStatusValue = (status: TrailerDoorStatus) => {
  const {Offline} = TrailerDoorStatus;
  const _status = !isNil(status) ? status : Offline;
  return translate(`common:smarterTrailer.door.status.${_status}`);
};

/**
 * Defines a utility to return Door Status data
 *
 * ```typescript
 * import {getDoorStatus} from '@ion/dls/src/components/smarterTrailer';
 *
 * const doorStatus = getDoorStatus(data);
 *
 * ```
 */
export const getDoorStatus = ({DoorState: doorState}: AugmentedSmarterTrailerSensorData): DoorHistoryItem => {
  return doorState?.doorHistory?.[0];
};

/**
 * Defines a utility to return Tire Pressure string labels
 *
 * ```typescript
 * import {getTirePressureLabels} from '@ion/dls/src/components/smarterTrailer';
 *
 * const {
 *   label,
 *   highPressure,
 *   normalPressure,
 *   lowPressure
 * } = getTirePressureLabels();
 *
 * ```
 */
export const getTirePressureLabels = () => {
  return {
    label          : translate('common:smarterTrailer.tires.pressure.label'),
    highPressure   : translate('common:smarterTrailer.tires.pressure.status.high'),
    normalPressure : translate('common:smarterTrailer.tires.pressure.status.normal'),
    lowPressure    : translate('common:smarterTrailer.tires.pressure.status.low')
  };
};

/**
 * Defines a utility to return Tire Pressure data
 *
 * ```typescript
 * import {getTirePressure} from '@ion/dls/src/components/smarterTrailer';
 *
 * const tirePressure = getTirePressure(data, 'label');
 *
 * ```
 */
export const getTirePressure = (tires: AugmentedSmarterTrailerSensorProperty): SmarterTrailerWidgetInfo => {
  return getProperty({
    item     : tires,
    property : 'pressure',
    issues   : [
      SmarterTrailerIssueTypes.lowPressure,
      SmarterTrailerIssueTypes.highPressure,
      SmarterTrailerIssueTypes.lowPressureAlarm,
      SmarterTrailerIssueTypes.highPressureAlarm
    ]
  });
};

/**
 * Defines a utility to return Tire Temperature string labels
 *
 * ```typescript
 * import {getTemperatureLabels} from '@ion/dls/src/components/smarterTrailer';
 *
 * const {
 *   label,
 *   highTemp,
 *   normalTemp
 * } = getTemperatureLabels();
 *
 * ```
 */
export const getTemperatureLabels = (sensorType) => {
  return {
    label : (sensorType === SensorTypes.TPMS)
      ? translate('common:smarterTrailer.tires.temperature.shortLabel')
      : translate('common:smarterTrailer.hubs.hubTemp'),
    highTemp   : translate('common:smarterTrailer.tires.temperature.status.high'),
    normalTemp : translate('common:smarterTrailer.tires.temperature.status.normal')
  };
};

/**
 * Defines a utility to return Tire Temperature data
 *
 * ```typescript
 * import {getTemperature} from '@ion/dls/src/components/smarterTrailer';
 *
 * const tireTemperature = getTemperature(data, 'label');
 *
 * ```
 */
export const getTemperature = (tires: AugmentedSmarterTrailerSensorProperty): SmarterTrailerWidgetInfo => {
  return getProperty({
    item       : tires,
    property   : 'temperature',
    issues     : [SmarterTrailerIssueTypes.highTemperature, SmarterTrailerIssueTypes.temperatureAlarm],
    includeMin : false
  });
};

const getProperty = ({
  item,
  property,
  issues: issueTypes,
  includeMin = true
}: PropertyType): SmarterTrailerWidgetInfo => {
  if (!isEmpty(item)) {
    const values = Object.values(item);
    const max = getMaximumBy(values, val => Number(val?.[property]?.value));
    const min = includeMin ? getMinimumBy(values, val => Number(val?.[property]?.value)) : {[property]: {value: 0}};
    const units = max?.[property].units;
    const maxValue = max?.[property].value;
    const minValue = min?.[property].value;

    const issues = {};
    values.forEach(tire => {
      issueTypes.forEach(issue => {
        if (tire[issue]) {
          issues[issue] = true;
        }
      });
    });
    return {
      minValue    : includeMin ? Number(minValue) || null : undefined,
      maxValue    : Number(maxValue) || null,
      messageTime : values?.[0]?.messageTime,
      issues      : issues || null,
      units       : units || null
    };
  }
  return null;
};

/**
 * Defines a utility to return the message time for the Cargo Sensor
 *
 * ```typescript
 *
 * import {getCargoSensorMessageTime} from '@ion/dls/src/components/smarterTrailer';
 *
 * const messageTime = getCargoSensorMessageTime(data);
 *
 * ```
 * @param data
 * @returns Cargo Sensor timestamp
 */
export const getCargoSensorMessageTime = (data: GenericObject): SmarterTrailerWidgetInfo => {
  return data?.[0]?.messageTime || null;
};

export const getBrakeStatus = (brakeData: AugmentedBrakeData) => {
  const {
    parkingBrakeStatus = null,
    isDragAlert = false
  } = brakeData;
  return isDragAlert ? BrakeChamberStatusType.Drag : parkingBrakeStatus;
};

export const isParkingBrakeLock = (value: BrakeChamberStatusType) => (value === BrakeChamberStatusType.Engaged);

export const getParkingDragStatus = (parkingBrakeStatus: BrakeChamberStatusType, assetData: BaseAsset) => {
  if (assetData) {
    const {gpsValidity, speed} = assetData;
    return isParkingBrakeLock(parkingBrakeStatus) && (speed > 5 && !!gpsValidity);
  }
  return false;
};

/**
 * Converts a pressure value to a preferred unit of measurement in the augmentation data
 * as well as additional check in the widget detail table.
 *
 * @param {Object} sensorDetail - An object with a `value` (number) and `units` (string) property.
 * @param {string} preferredUnit - A string representing the preferred unit of measurement, either 'PSI' or 'KPA'.
 * @returns {Object|undefined} - An object with a `value` (number) and `units` (string) property.
 * Returns undefined if the preferred unit is not 'PSI' or 'KPA'.
 */
export const convertPressureValueAndUnits = (
  sensorDetail,
  preferredUnit = SensorUnits.PSI
) => {
  const {value, units} = sensorDetail;
  const parsedValue = parseInt(value);
  if (isNaN(parsedValue)) {
    return {
      value : null,
      units : ''
    };
  }
  if (preferredUnit === units) {
    return {
      value : parsedValue,
      units
    };
  }

  let convertedValue: number;
  switch (preferredUnit) {
    case SensorUnits.PSI:
      convertedValue = Math.round(parsedValue * 0.145038);
      break;
    case SensorUnits.KPA:
      convertedValue = Math.round(parsedValue * 6.89476);
      break;
  }

  return {
    value : convertedValue,
    units : preferredUnit
  };
};

/**
 * getRemainingCargoAvailability returns the remaining availability of the Cargo Sensors or
 * null if the data is not available.
 *
 * ```typescript
 *
 * import {getRemainingCargoAvailability} from '@ion/dls/src/components/smarterTrailer';
 *
 * const remainingAvailability = getRemainingCargoAvailability(data);
 *
 * ```
 *
 * @param cargoData smart trailer cargo sensor data
 * @returns number || null
 */
export const getRemainingCargoAvailability = (cargoData: GenericObject) => {
  if (!isNil(cargoData) && !isEmpty(cargoData)) {
    const values = Object?.values(cargoData).map((section) => Number(section?.height?.value));
    const trailerLoad = values?.reduce((a, b) => a + b, 0);
    const totalAvailability = values?.length * 100;
    if (trailerLoad === totalAvailability) {
      return 0;
    }
    const remainingAvailability = (trailerLoad / totalAvailability) * 100;
    if (remainingAvailability >= 0) {
      return Math.round(remainingAvailability);
    }
  }
  return null;
};

/**
 * getTimeDifference returns the time difference between the current time and the message time
 *
 * @param timestamp the event timestamp
 * @returns the time difference between the current time and the event timestamp or null if it the event
 * didn't happen in the last 24 hours
 */
export const getTimeDifference = (timestamp) => {
  if (!isNil(timestamp)) {
    const now         = new Date();
    const eventTime   = new Date(timestamp);
    const timeDiff    = Math.abs(now.getTime() - eventTime?.getTime());
    const diffMinutes = Math.ceil(timeDiff / (1000 * 60));
    const diffHours   = Math.ceil(diffMinutes / 60);
    if (diffMinutes < 60) {
      return translate('common:smarterTrailer.widgets.time.minutesAgo', {time: diffMinutes});
    }
    if (diffHours === 1) {
      return translate('common:smarterTrailer.widgets.time.hourAgo', {time: diffHours});
    }
    if (diffHours < 24) {
      return translate('common:smarterTrailer.widgets.time.hoursAgo', {time: diffHours});
    }
    return null;
  }
  return null;
};

/**
 * @getWeightSensorMessageTime returns the message time for the Weight Sensor
 *
 * ```typescript
 *
 * import {getWeightSensorMessageTime} from '@ion/dls/src/components/smarterTrailer';
 *
 * const messageTime = getWeightSensorMessageTime(data);
 *
 * ```
 * @param data
 * @returns Weight Sensor timestamp
 *
 */
export const getWeightSensorMessageTime = (data: GenericObject) => {
  return data?.WE1?.messageTime || null;
};

/**
 * convertWeightSensorData returns the weight sensor data after converting it to the correct units
 *
 * ```typescript
 *
 * import {convertWeightSensorData} from '@ion/dls/src/components/smarterTrailer';
 * const userUnits = 'US';
 *
 * const {value, units} = convertWeightSensorData(data, userUnits); // {1000, 'lbs'}
 *
 * ```
 */
export const convertWeightSensorData = (data: GenericObject, userUnits: string) => {
  if (!isNil(data)) {
    const {value, units} = data?.WE1 || {};
    const parsedValue    = Number(value);
    const us             = Unit.US?.toLowerCase();
    const preferredUnits = userUnits?.toLowerCase() === us ? us : WeightUnit.kg;
    // if the units are the same, return the value and the units from the response
    if (preferredUnits === units) {
      return {
        value : parsedValue,
        units
      };
    }
    // if the units are different, convert the value and return the new value and the preferred units
    if (preferredUnits === us && units === WeightUnit.kg) {
      return {
        value : kgToLbs(parsedValue),
        units : WeightUnit.lbs
      };
    }
    if (preferredUnits === WeightUnit.kg && units === WeightUnit.lbs) {
      return {
        value : lbsToKg(parsedValue),
        units : WeightUnit.kg
      };
    }
  }
  return {
    value : translate('common:notAvailable'),
    units : ''
  };
};

/**
 * getLightOutDetectionMessageTime returns the message time for the light out detection
 *
 * ```typescript
 *
 * import {getLightOutDetectionMessageTime} from '@ion/dls/src/components/smarterTrailer';
 *
 * const messageTime = getLightOutDetectionMessageTime(data);
 *
 * ```
 *
 * @param data the light out detection data
 */
export const getLightOutDetectionMessageTime = (data: GenericObject) => {
  return data?.LA1?.messageTime || null;
};

/**
 * @isCircuitConnected returns true if the circuit is connected
 */
export const isCircuitConnected = (circuit: CircuitStatus) => {
  return (
    circuit === CircuitStatus.GOOD || circuit === CircuitStatus.RECONNECTED
  );
};

/**
 * @isCircuitUnknown returns true if the circuit is unknown
 */
export const isCircuitUnknown = (circuit: CircuitStatus) => {
  return circuit === CircuitStatus.UNKNOWN;
};

/**
 * @isCircuitFaulty returns true if the circuit is faulty
 */
export const isCircuitFaulty = (circuit: CircuitStatus) => {
  const disconnected = CircuitStatus.DISCONNECTED;
  const lowCurrent   = CircuitStatus.LOW_CURRENT;
  const highCurrent  = CircuitStatus.HIGH_CURRENT;
  const intermittent = CircuitStatus.INTERMITTENT;
  return (
    circuit === disconnected ||
    circuit === lowCurrent ||
    circuit === highCurrent ||
    circuit === intermittent
  );
};

/**
 * @validateCicuitFault returns true if the circuit is faulty
 *
 * ```typescript
 *
 * import {validateCicuitFault} from '@ion/dls/src/components/smarterTrailer';
 *
 * const isFaulty = validateCicuitFault(circuit);
 *
 * ```
 */
export const validateCicuitFault = (circuit: CircuitStatus) => {
  return isCircuitFaulty(circuit);
};

/**
 * validateLightOutDetectionConnection returns true if the light out detection data is available
 *
 * ```typescript
 *
 * import {validateLightOutDetectionConnection} from '@ion/dls/src/components/smarterTrailer';
 *
 * const isConnected = validateLightOutDetectionConnection(data);
 *
 * ```
 *
 * @param data the light out detection data
 */
export const validateLightOutDetectionConnection = (data: GenericObject) => {
  const {
    clearanceLampCircuitStatus,
    rightTurnLampCircuitStatus,
    leftTurnLampCircuitStatus,
    tailLampCircuitStatus,
    stopLampCircuitStatus
  } = data;

  const circuits = [
    clearanceLampCircuitStatus,
    rightTurnLampCircuitStatus,
    leftTurnLampCircuitStatus,
    tailLampCircuitStatus,
    stopLampCircuitStatus
  ];

  const hasFault = circuits.some(circuit => isCircuitFaulty(circuit));
  if (hasFault) {
    return CircuitSummary.FAULT;
  }

  const allCircuitsUnknown = circuits.every(circuit => isCircuitUnknown(circuit));
  if (allCircuitsUnknown) {
    return CircuitSummary.UNKNOWN;
  }

  const someCircuitsConnected = circuits.some(circuit => !isCircuitConnected(circuit));
  if (someCircuitsConnected) {
    return CircuitSummary.UNKNOWN;
  }

  return CircuitSummary.GOOD;
};

/**
 * @validateUnknowCircuit returns the correct status for circuits that we do not have data for like Ground and Auxillary
 *
 * ```typescript
 *
 * import {validateUnknowCircuit} from '@ion/dls/src/components/smarterTrailer';
 *
 * const status = validateUnknowCircuit(circuitData);
 *
 * ```
 *
 * @param circuitData the circuit data
 */
export const validateUnknownCircuit = ([...circuitData]: Array<CircuitStatus> = []) => {
  const areCircuitsConnected = circuitData.every(circuit => isCircuitConnected(circuit) || isCircuitUnknown(circuit));
  const areCircuitsDisconnected = circuitData.every(circuit => isCircuitFaulty(circuit));
  const circuitsAreUndefined = circuitData.every(circuit => isNil(circuit));
  if (circuitData.length === 0 || circuitsAreUndefined) {
    return CircuitStatus.DISCONNECTED;
  }
  if (areCircuitsDisconnected) {
    return CircuitStatus.DISCONNECTED;
  }
  if (areCircuitsConnected) {
    return CircuitStatus.GOOD;
  }
  return CircuitStatus.UNKNOWN;
};
